import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalContent
} from "@heroui/react";
import {useState, useEffect} from "react";

export default function FindAndReplaceModal({
                                                isOpen,
                                                onOpenChange,
                                                projectForm,
                                                onConfirm
                                            }) {
    const [originalText, setOriginalText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [matchInfo, setMatchInfo] = useState({count: 0, indices: []});
    const [showHighlights, setShowHighlights] = useState(false);
    const [excludedIndices, setExcludedIndices] = useState([]);
    const [replaceTextError, setReplaceTextError] = useState(false);
    const [originalTextError, setOriginalTextError] = useState(false);
    const [isFindLoading, setIsFindLoading] = useState(false);

    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Unicode-aware "whole word" matcher. JS's \b only treats [A-Za-z0-9_] as word
    // chars, so it never matches accented Latin (café) or non-Latin scripts (Hindi,
    // Arabic, …) — which is exactly what translated subtitles contain. We use
    // \p{L}/\p{N} lookarounds instead, and fall back to plain substring matching for
    // space-less scripts (CJK, Thai) where "word boundaries" don't apply.
    const buildPattern = (term) => {
        const escaped = escapeRegExp(term);
        const noSpaceScript = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Script=Thai}]/u.test(term);
        if (noSpaceScript) return escaped;
        const left = /[\p{L}\p{N}]/u.test(term[0]) ? '(?<![\\p{L}\\p{N}])' : '';
        const right = /[\p{L}\p{N}]/u.test(term[term.length - 1]) ? '(?![\\p{L}\\p{N}])' : '';
        return `${left}${escaped}${right}`;
    };

    const findMatches = (originalText) => {
        let count = 0;
        const indices = [];
        const regex = new RegExp(buildPattern(originalText), 'giu');

        projectForm.values.text_srt_json.forEach((segment, index) => {
            const matches = segment.translated_text.match(regex);
            if (matches) {
                count += matches.length;
                indices.push(index);
            }
        });
        setMatchInfo({count, indices});
    };

    const highlightText = (text, highlight) => {
        if (!highlight) return text;
        const parts = text.split(new RegExp(`(${buildPattern(highlight)})`, 'giu'));
        return parts.map((part, index) =>
          part.toLowerCase() === highlight.toLowerCase() ?
            <span key={index} className="bg-yellow-300 px-1 rounded-lg font-bold">{part}</span> : part
        );
    };

    useEffect(() => {
        setIsFindLoading(false);
    }, [matchInfo]);

    const handleFindAndReplace = () => {
        const regex = new RegExp(buildPattern(originalText), 'giu');
        const updatedTextSrtJson = projectForm.values.text_srt_json.map((segment, index) => {
            if (!excludedIndices.includes(index) && regex.test(segment.translated_text)) {
                regex.lastIndex = 0;
                return {
                    ...segment,
                    translated_text: segment.translated_text.replace(regex, replaceText),
                    is_audio_changed: true
                };
            }
            return segment;
        });

        projectForm.setFieldValue('text_srt_json', updatedTextSrtJson);
        projectForm.setFieldValue('is_change_available', true);
        handleClose();
        onConfirm?.();
    };

    const handleRemoveWord = (index) => {
        setExcludedIndices([...excludedIndices, index]);
        setMatchInfo((prev) => ({
            ...prev,
            indices: prev.indices.filter((i) => i !== index),
        }));
    };

    const handleShowHighlights = () => {
        const trimmed = originalText.trim();
        setOriginalText(trimmed);
        setShowHighlights(true);
        findMatches(trimmed);
    };

    const handleConfirm = () => {
        if (matchInfo.indices?.length > 0 && replaceText?.length > 0) {
            handleFindAndReplace();
        } else {
            setReplaceTextError(true);
        }
    };

    const handleClose = () => {
        setOriginalText('');
        setReplaceText('');
        setShowHighlights(false);
        setExcludedIndices([]);
        setMatchInfo({count: 0, indices: []});
        onOpenChange();
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        size="2xl"
        hideCloseButton
        backdrop="blur"
        classNames={{
            base: "overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl",
            body: "p-0",
        }}
      >
          <ModalContent>
              {(onClose) => (
                <ModalBody>
                    {/* Hero band */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#7659fa] via-[#8a6cff] to-[#a78bfa] px-8 pt-8 pb-9">
                        <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                        <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                        <div className="relative flex items-center gap-4">
                            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
                                <i className="fa-solid fa-magnifying-glass text-2xl text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-white">Find &amp; Replace</h2>
                                <p className="mt-0.5 text-sm font-light text-white/80">
                                    Swap a word everywhere across your subtitles in one pass.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Inputs */}
                    <div className="px-6 pt-6">
                        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end">
                            <div className="flex-1">
                                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Find</label>
                                <Input
                                  placeholder="Word to find"
                                  startContent={<i className="fa-solid fa-magnifying-glass text-xs text-slate-400" />}
                                  classNames={{
                                      inputWrapper: `h-[44px] bg-[#fcfcfd] rounded-xl border ${originalTextError ? "border-red-500" : "border-[#12131714]"} focus-within:!border-[#7659fa]`,
                                      input: "!text-sm text-slate-700 placeholder:text-gray-400",
                                  }}
                                  value={originalText}
                                  onChange={(e) => {
                                      setOriginalText(e.target.value);
                                      setOriginalTextError(false);
                                  }}
                                />
                            </div>

                            <div className="hidden shrink-0 pb-3 sm:block">
                                <i className="fa-solid fa-arrow-right-arrow-left text-slate-300" />
                            </div>

                            <div className="flex-1">
                                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Replace with</label>
                                <Input
                                  placeholder="New word"
                                  startContent={<i className="fa-solid fa-pen text-xs text-slate-400" />}
                                  classNames={{
                                      inputWrapper: `h-[44px] bg-[#fcfcfd] rounded-xl border ${replaceTextError ? "border-red-500" : "border-[#12131714]"} focus-within:!border-[#7659fa]`,
                                      input: "!text-sm text-slate-700 placeholder:text-gray-400",
                                  }}
                                  value={replaceText}
                                  onChange={(e) => {
                                      setReplaceText(e.target.value);
                                      setReplaceTextError(false);
                                  }}
                                />
                            </div>

                            <Button
                              isLoading={isFindLoading}
                              className="h-[44px] shrink-0 rounded-xl bg-[#7659fa] px-5 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95"
                              onPress={() => {
                                  if (originalText?.length === 0) {
                                      setOriginalTextError(true);
                                  } else {
                                      setIsFindLoading(true);
                                      handleShowHighlights();
                                  }
                              }}
                            >
                                <i className="fa-solid fa-magnifying-glass text-xs" /> Find
                            </Button>
                        </div>

                        {/* Match summary chip */}
                        {showHighlights && matchInfo.indices?.length > 0 && (
                          <div className="mt-4 flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#7659fa1a] px-3 py-1 text-xs font-medium text-[#7659fa]">
                                  <i className="fa-solid fa-circle-check" />
                                  {matchInfo.count} match{matchInfo.count === 1 ? "" : "es"} in {matchInfo.indices.length} segment{matchInfo.indices.length === 1 ? "" : "s"}
                              </span>
                              <span className="text-xs font-light text-slate-400">Tap ✕ to skip a segment</span>
                          </div>
                        )}
                    </div>

                    {/* Results */}
                    <div className="px-6 pb-2 pt-3">
                        {showHighlights && matchInfo.indices?.length === 0 && (
                          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-[#fcfcfd] py-10 text-center">
                              <i className="fa-solid fa-magnifying-glass-minus mb-3 text-2xl text-slate-300" />
                              <p className="text-sm font-medium text-slate-600">No matches found</p>
                              <p className="mt-1 text-xs font-light text-slate-400">Try a different word or check the spelling.</p>
                          </div>
                        )}
                        {showHighlights && matchInfo.indices?.length > 0 && (
                          <div className="max-h-64 space-y-2 overflow-y-auto scrollbar-thin pr-1">
                              {matchInfo.indices.map((index) => (
                                <div key={index}
                                     className="group flex items-center justify-between gap-3 rounded-xl border border-[#12131714] bg-[#fcfcfd] py-2.5 px-3 transition hover:border-[#7659fa40]">
                                    <div className="text-[14px] leading-relaxed text-slate-700">
                                        {highlightText(projectForm.values.text_srt_json[index].translated_text, originalText)}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="flat"
                                      isIconOnly
                                      title="Skip this segment"
                                      className="h-7 w-7 shrink-0 rounded-full bg-slate-200 text-slate-500 transition hover:bg-red-500 hover:text-white"
                                      onPress={() => handleRemoveWord(index)}
                                    >
                                        <i className="fa-solid fa-xmark text-[13px]" />
                                    </Button>
                                </div>
                              ))}
                          </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 px-6 py-5">
                        <Button
                          onPress={handleClose}
                          variant="bordered"
                          className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
                            Cancel
                        </Button>
                        <Button
                          isDisabled={!matchInfo.indices?.length}
                          className={`h-11 rounded-xl px-6 text-sm font-medium text-white transition ${matchInfo.indices?.length ? "bg-[#7659fa] shadow-lg shadow-[#7659fa40] hover:opacity-95" : "bg-[#b5a7f2] cursor-not-allowed"}`}
                          onPress={handleConfirm}
                        >
                            <i className="fa-solid fa-wand-magic-sparkles text-xs" /> Replace{matchInfo.indices?.length ? ` ${matchInfo.indices.length}` : ""}
                        </Button>
                    </div>
                </ModalBody>
              )}
          </ModalContent>
      </Modal>
    );
}